// hooks/useInlineImages.js
import {useCallback, useState} from 'react';
import {fetchInlineImagesApi} from '../service/api';

export const useInlineImages = (apiUrl) => {
  const [inlineImagesMap, setInlineImagesMap] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageNote, setSelectedImageNote] = useState(null);

  const fetchInlineImages = useCallback(async (noteId) => {
    if (!noteId || loadingImages[noteId]) return;
    setLoadingImages(prev => ({ ...prev, [noteId]: true }));
    try {
      const images = await fetchInlineImagesApi(noteId, apiUrl);
      setInlineImagesMap(prev => ({ ...prev, [noteId]: images }));
      return images;
    } catch (error) {
      console.error(`Error fetching images for note ${noteId}:`, error);
      setInlineImagesMap(prev => ({ ...prev, [noteId]: [] }));
      return [];
    } finally {
      setLoadingImages(prev => ({ ...prev, [noteId]: false }));
    }
  }, [apiUrl, loadingImages]);

  const handleImageThumbnailClick = useCallback(async (note, imageIndex = 0) => {
    if (!note.inlineImageCount) return;
    let images = inlineImagesMap[note.id];
    if (!images?.length) {
      images = await fetchInlineImages(note.id);
    }
    if (images?.length) {
      setSelectedImageNote(note);
      setSelectedImage({ image: images[imageIndex], index: imageIndex, total: images.length });
      setShowImageViewer(true);
    }
  }, [inlineImagesMap, fetchInlineImages]);

  const handleNextImage = useCallback(() => {
    if (selectedImage && selectedImageNote) {
      const images = inlineImagesMap[selectedImageNote.id];
      if (images?.length) {
        const nextIndex = (selectedImage.index + 1) % images.length;
        setSelectedImage({ ...selectedImage, image: images[nextIndex], index: nextIndex });
      }
    }
  }, [selectedImage, selectedImageNote, inlineImagesMap]);

  const handlePrevImage = useCallback(() => {
    if (selectedImage && selectedImageNote) {
      const images = inlineImagesMap[selectedImageNote.id];
      if (images?.length) {
        const prevIndex = (selectedImage.index - 1 + images.length) % images.length;
        setSelectedImage({ ...selectedImage, image: images[prevIndex], index: prevIndex });
      }
    }
  }, [selectedImage, selectedImageNote, inlineImagesMap]);

  const closeImageViewer = useCallback(() => {
    setShowImageViewer(false);
    setSelectedImage(null);
    setSelectedImageNote(null);
  }, []);

  return {
    inlineImagesMap,
    loadingImages,
    showImageViewer,
    selectedImage,
    selectedImageNote,
    fetchInlineImages,
    handleImageThumbnailClick,
    handleNextImage,
    handlePrevImage,
    closeImageViewer,
  };
};